<template>
  <div class="preview-container">
    <ChangeBrandsPopUp
      :isOpen="showPopup"
      @accept="acceptUpdateBrands"
      @close="cancelUpdateBrands">
    </ChangeBrandsPopUp>
    <div class="main-header">
      Report Preview
    </div>
    <div class="card">
      <div class="card-title">
        Brand
      </div>
      <div class="brand-dropdown">
        <FilterSelect
          :value="localSelectedBrands"
          :dimension="brandObject"
          :portfolioBrands="portfolioBrands"
          :showBadge="true"
          :error="hasBrandError"
          @input="updateBrands($event)"
        ></FilterSelect>
      </div>
    </div>
    <div class="card">
      <div class="card-title">
        Dimension
      </div>
      <div
        class="card-content"
        id="dimension-preview-card">
        <input
          v-if="selectedDimension"
          :value="selectedDimensionDisplayValue"
          type="text"
          readonly
        />
        <div v-else class="no-selected-msg">
          {{ noSelectedMsg }}
        </div>
      </div>
    </div>
    <div class="card">
      <div class="card-title">
        Metrics
      </div>
      <div class="card-content">
        <DragAndDrop
          v-bind:values="localSelectedMetrics"
          :noOptionsMessage="noSelectedMsg"
          @input="$emit('updateMetrics', $event)"
        />
      </div>
    </div>
    <div>
      <div class="card-title">
        Filters
        <div
          v-if="advancedFilters.length"
          class="remove-all"
          @click="$emit('removeAllAdvancedFilters')"
        >
          REMOVE ALL
        </div>
      </div>
      <div
        v-if="basicFilters.length"
      >
        <FilterBadge
          v-for="filter in basicFilters"
          :key="filter.key"
          :filter="filter"
          type="dismiss"
          @dismiss="$emit('removeFilter', filter)"
          @click="$emit('openFilters')"
        />
      </div>
      <div
        v-else-if="advancedFilters.length"
      >
        <AdvancedFilterSection
          :initialText="advancedFilterInitialText"
          :advancedFilters="advancedFilters"
          @openFilters="$emit('openFilters')"
        />
      </div>
      <div
        v-else-if="showFilters"
        class="no-selected-msg"
      >
        {{ noSelectedMsg }}
      </div>
      <SecondaryButton
        v-else
        class="add-filters-button"
        @click="$emit('addFilters')"
      >
        Add Filters
      </SecondaryButton>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import { SecondaryButton } from '@mikmak/components';
import DragAndDrop from '../../../../Common/DragAndDrop.vue';
import FilterBadge from '../../../../Common/Badges/FilterBadge/FilterBadge.vue';
import FilterSelect from '../../../../Common/FilterSelect.vue';
import AdvancedFilterSection from './AdvancedFilterSection.vue';
import ChangeBrandsPopUp from './ChangeBrandsPopup.vue';
import {
  getReportFieldFromValue,
} from '../../../../../../services/dashboard/reportFieldService.js';

export default {
  props: {
    selectedBrands: {
      type: Array,
    },
    selectedDimension: {
      type: String,
      default: '',
    },
    selectedMetrics: {
      type: Array,
    },
    selectedBasicFilters: {
      type: Array,
    },
    selectedAdvancedFilters: {
      type: Array,
    },
    portfolioBrands: {
      type: Array,
    },
    availableShopperIntelligenceSegments: {
      type: Array,
    },
    showFilters: {
      type: Boolean,
    },
    filterType: {
      type: String,
    },
  },
  data() {
    return {
      noSelectedMsg: 'None Selected',
      localSelectedMetrics: this.selectedMetrics || [],
      localSelectedBasicFilters: this.selectedBasicFilters || [],
      localSelectedAdvancedFilters: this.selectedAdvancedFilters || [],
      isBrandError: false,
      showPopup: false,
      localSelectedBrands: cloneDeep(this.selectedBrands),
      advancedFilterInitialText: 'Filter on users whose',
      advancedOperators: {
        'is': {
          label: 'is',
          typeText: 'or',
        },
        'isNot': {
          label: 'is not',
          typeText: 'or',
        },
        'contains': {
          label: 'contains',
          typeText: 'and',
        },
        'doesNotContain': {
          label: 'does not contain',
          typeText: 'and',
        },
      },
    };
  },
  components: {
    SecondaryButton,
    DragAndDrop,
    FilterBadge,
    FilterSelect,
    AdvancedFilterSection,
    ChangeBrandsPopUp,
  },
  computed: {
    ...mapState({
      allSegments: state => state.dashboard.availableReportFields.allSegmentsDimension,
      allReportFields: state => state.dashboard.availableReportFields.allReportFields,
    }),
    brandObject() {
      return getReportFieldFromValue(this.allReportFields, 'brandName');
    },
    basicFilters() {
      return this.filterType === 'Basic' ? this.localSelectedBasicFilters : [];
    },
    advancedFilters() {
      if (this.filterType === 'Basic') return [];

      return this.localSelectedAdvancedFilters.reduce((groupsAcc, group) => {

        const groupToDisplay = group.reduce((filtersAcc, filter) => {
          const filterToDisplay = this.reformatFilter(filter);

          if (filterToDisplay) filtersAcc.push(filterToDisplay);

          return filtersAcc;
        }, []);

        if (groupToDisplay.length > 0) groupsAcc.push(groupToDisplay);

        return groupsAcc;
      }, []);
    },
    hasBrandError() {
      return this.selectedBrands && this.selectedBrands.length === 0;
    },
    selectedDimensionDisplayValue() {
      if (this.selectedDimension === this.allSegments.value) {
        return `All Segments (${this.availableShopperIntelligenceSegments.length})`;
      }

      const reportField = getReportFieldFromValue(this.allReportFields, this.selectedDimension);

      return reportField.key;
    },
  },
  methods: {
    updateBrands(val) {
      this.showPopup = this.basicFilters.length > 0 || this.advancedFilters.length > 0;
      this.localSelectedBrands = val[this.brandObject.value];

      if (!this.showPopup) {
        this.$emit('updateBrands', cloneDeep(this.localSelectedBrands));
      }
    },
    acceptUpdateBrands() {
      this.showPopup = false;
      this.$emit('updateBrands', cloneDeep(this.localSelectedBrands));
    },
    cancelUpdateBrands() {
      this.showPopup = false;
      this.localSelectedBrands = cloneDeep(this.selectedBrands);
    },
    reformatFilter(filter) {
      if (filter && filter.key && filter.operator && filter.value &&
        filter.value.length && this.advancedOperators[filter.operator]) {
        const dim = getReportFieldFromValue(this.allReportFields, filter.key);

        return {
          name: dim.key,
          operator: this.advancedOperators[filter.operator].label,
          firstValue: filter.value[0],
          secondValue: filter.value[1] ? filter.value[1] : null,
          condition: filter.value[1] ? this.advancedOperators[filter.operator].typeText : null,
          extraValues: filter.value.length > 2 ? filter.value.length - 1 : 0,
        };
      }

      return null;
    },
  },
  watch: {
    selectedMetrics() {
      this.localSelectedMetrics = this.selectedMetrics;
    },
    selectedBasicFilters() {
      this.localSelectedBasicFilters = this.selectedBasicFilters;
    },
    selectedAdvancedFilters() {
      this.localSelectedAdvancedFilters = this.selectedAdvancedFilters;
    },
  },
};
</script>
<style lang="scss" scoped>
.preview-container {
  margin-left: 24px;
  padding: 16px;
  min-height: calc(100vh - 124px);
  width: 400px;
  background: #F7FAFC;
  border: 1px solid #C1CCD4;
  box-sizing: border-box;
  border-radius: 4px;
  overflow-y: auto;
}
.main-header {
  font-weight: normal;
  font-size: 24px;
  line-height: 140%;
  color: #223448;
}
.card-title {
  font-weight: bold;
  font-size: 16px;
  line-height: 140%;
  color: #223448;
  padding-top: 24px;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;

  .remove-all {
    font-weight: 400;
    font-size: 12px;
    line-height: 17px;
    letter-spacing: 0.5px;
    color: #778699;
    text-decoration: underline;
    cursor: pointer;
  }
}
.card {
  width: 100%;
  border-bottom: 1px solid #C1CCD4;
  padding-bottom: 24px;
}
.card-content {
  min-height: 44px;

  input {
    height: 44px;
    width: 100%;
    padding: 14px 12px;
    background: #FFFFFF;
    border: 1px solid #C1CCD4;
    box-sizing: border-box;
    border-radius: 4px;
    font-weight: normal;
    font-size: 16px;
    line-height: 100%;
    color: #223448;
    text-transform: capitalize;
    font-family: Lato;
    cursor: default;
  }
}
.no-selected-msg {
  font-weight: normal;
  font-size: 16px;
  line-height: 140%;
  text-align: center;
  color: #778699;
}

.add-filters-button {
  width: 100%;
}

input:focus {
  outline: none;
}
</style>
